/*
 * Custom Back-in-Stock modal logic.
 * Replaces the native Klaviyo BIS widget (klaviyo.enable("backinstock", ...))
 * with a multi-language version that POSTs directly to Klaviyo's client API.
 *
 * Endpoint contract verified live against mokebo's production Klaviyo widget
 * (2026-05-26). See snippets/bis-modal.liquid for markup + styles.
 */
(function () {
  'use strict';

  var CONFIG = {
    klaviyoCompanyId: 'XpLQGB',
    klaviyoRevision: '2025-04-15',
    bisEndpoint: 'https://a.klaviyo.com/client/back-in-stock-subscriptions/',
    subscriptionsEndpoint: 'https://a.klaviyo.com/client/subscriptions/'
  };

  var modal = null;
  var formEl = null;
  var successEl = null;
  var errorEl = null;
  var emailInput = null;
  var newsletterInput = null;
  var submitBtn = null;
  var triggerSelector = '.klaviyo-bis-trigger';
  var lastFocusedBeforeOpen = null;

  function getCurrentVariantId() {
    // Active product-form's variant id input. Focal updates this on variant change.
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (input && input.value) return input.value;
    // Fallback: variant query param.
    var match = /[?&]variant=(\d+)/.exec(window.location.search);
    return match ? match[1] : null;
  }

  function postBackInStock(email, variantId) {
    var url = CONFIG.bisEndpoint + '?company_id=' + CONFIG.klaviyoCompanyId + '&revamp=true';
    var body = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: { type: 'profile', attributes: { email: email } }
          }
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: '$shopify:::$default:::' + variantId
            }
          }
        }
      }
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'revision': CONFIG.klaviyoRevision
      },
      body: JSON.stringify(body)
    });
  }

  function postNewsletterSubscription(email) {
    var listId = modal && modal.dataset.newsletterListId;
    if (!listId) return Promise.resolve(null);
    var url = CONFIG.subscriptionsEndpoint + '?company_id=' + CONFIG.klaviyoCompanyId;
    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock Modal',
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                subscriptions: {
                  email: { marketing: { consent: 'SUBSCRIBED' } }
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'revision': CONFIG.klaviyoRevision
      },
      body: JSON.stringify(body)
    });
  }

  function showForm() {
    if (formEl) formEl.hidden = false;
    if (successEl) successEl.hidden = true;
    if (errorEl) errorEl.hidden = true;
  }

  function openModal() {
    if (!modal) return;
    var variantId = getCurrentVariantId();
    if (!variantId) return;
    lastFocusedBeforeOpen = document.activeElement;
    if (emailInput) emailInput.value = '';
    if (newsletterInput) newsletterInput.checked = false;
    showForm();
    modal.hidden = false;
    document.body.style.overflow = 'hidden';
    setTimeout(function () { if (emailInput) emailInput.focus(); }, 50);
  }

  function closeModal() {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.body.style.overflow = '';
    if (lastFocusedBeforeOpen && typeof lastFocusedBeforeOpen.focus === 'function') {
      lastFocusedBeforeOpen.focus();
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!emailInput || !submitBtn) return;
    var email = (emailInput.value || '').trim();
    var variantId = getCurrentVariantId();
    if (!email || !variantId) return;
    var wantsNewsletter = !!(newsletterInput && newsletterInput.checked);

    submitBtn.disabled = true;
    if (errorEl) errorEl.hidden = true;

    postBackInStock(email, variantId)
      .then(function (resp) {
        if (resp.status !== 202 && resp.status !== 201 && resp.status !== 200) {
          throw new Error('BIS_HTTP_' + resp.status);
        }
        if (wantsNewsletter) {
          // Fire-and-forget: never block BIS success on newsletter failure.
          postNewsletterSubscription(email).catch(function (err) {
            if (window.console) console.warn('[BIS] newsletter sub failed', err);
          });
        }
        if (formEl) formEl.hidden = true;
        if (successEl) successEl.hidden = false;
      })
      .catch(function (err) {
        if (window.console) console.error('[BIS] subscribe failed', err);
        if (errorEl) errorEl.hidden = false;
      })
      .then(function () { submitBtn.disabled = false; });
  }

  function updateTriggerVisibility(available) {
    var triggers = document.querySelectorAll(triggerSelector);
    for (var i = 0; i < triggers.length; i++) {
      triggers[i].dataset.bisSoldOut = (!available).toString();
      triggers[i].style.setProperty('display', available ? 'none' : 'block', 'important');
    }
  }

  /*
   * Klaviyo's onsite SDK auto-injects a native BIS button driven by the BIS form
   * configured in Klaviyo Admin (Sign-up Forms) — independent of our theme code.
   * Until that form is unpublished, this shim removes Klaviyo's injection and
   * restores our anchor based on its data-bis-sold-out attribute (set by Liquid,
   * not mutated by Klaviyo). MutationObserver handles Klaviyo's async injection.
   */
  function reconcileBISButtons() {
    var pattern = /(benachrichtigen|notify|waarschuwen)/i;
    var btns = document.querySelectorAll('button.needsclick[class*="kl-private-reset"]');
    for (var i = 0; i < btns.length; i++) {
      var btn = btns[i];
      if (!pattern.test((btn.textContent || '').trim())) continue;
      var wrapper = btn;
      while (wrapper.parentElement && /needsclick/.test(wrapper.parentElement.className || '')) {
        wrapper = wrapper.parentElement;
      }
      wrapper.remove();
    }
    var triggers = document.querySelectorAll(triggerSelector);
    for (var j = 0; j < triggers.length; j++) {
      var t = triggers[j];
      if (t.dataset.bisSoldOut === 'true') {
        t.style.setProperty('display', 'block', 'important');
      } else {
        t.style.setProperty('display', 'none', 'important');
      }
    }
  }

  function init() {
    modal = document.getElementById('bis-modal');
    if (!modal) return;
    formEl = modal.querySelector('[data-bis-form]');
    successEl = modal.querySelector('[data-bis-success]');
    errorEl = modal.querySelector('[data-bis-error]');
    emailInput = modal.querySelector('input[type="email"]');
    newsletterInput = modal.querySelector('input[type="checkbox"]');
    submitBtn = modal.querySelector('button[type="submit"]');

    // Trigger click — event delegation so it works through dynamic re-renders.
    document.addEventListener('click', function (e) {
      var trigger = e.target.closest(triggerSelector);
      if (trigger) {
        e.preventDefault();
        openModal();
        return;
      }
      if (e.target.closest('[data-bis-close]')) {
        closeModal();
        return;
      }
      if (e.target === modal) closeModal();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hidden) closeModal();
    });

    document.addEventListener('variant:changed', function (e) {
      if (e.detail && e.detail.variant) {
        updateTriggerVisibility(e.detail.variant.available);
      }
    });

    if (formEl) formEl.addEventListener('submit', handleSubmit);

    // Reconcile against Klaviyo's async auto-injection.
    reconcileBISButtons();
    if (window.MutationObserver) {
      var obs = new MutationObserver(function () { reconcileBISButtons(); });
      obs.observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
